import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
  TextField,
  Paper,
  Alert,
} from "@mui/material";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const NotificationForm = () => {
  const [message, setMessage] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      setError("");

      try {
        const token = sessionStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const response = await fetch(`${BASE_URL}/api/employees`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch employee data");
        }

        const data = await response.json();
        const employeeData = data.map((employee) => ({
          value: employee.employeeid,
          label: `${employee.name} (${employee.employeeid})`,
        }));

        setEmployeeOptions([
          { value: "all", label: "Select All" },
          ...employeeData,
        ]);
      } catch (err) {
        console.error("Error fetching employee data:", err);
        setError(err.message || "Failed to fetch employee data");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleChange = (e) => setMessage(e.target.value);

  const handleEmployeeSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedEmployees([]);
      return;
    }

    if (selectedOptions.some((option) => option.value === "all")) {
      setSelectedEmployees(employeeOptions.filter((option) => option.value !== "all"));
      return;
    }

    setSelectedEmployees(selectedOptions.filter((option) => option.value !== "all"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    if (
      selectedEmployees.length === 0
    ) {
      setError("Please select at least one employee.");
      setIsSubmitting(false);
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const employeeIds = selectedEmployees.map((emp) => emp.value);

      const response = await fetch(`${BASE_URL}/api/notification/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message, employeeIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send notification");
      }

      const data = await response.json();
      if (data.success === true) {
        setSuccess("Notification sent successfully!");
        setShowSuccessSnackbar(true);
        setMessage("");
        setSelectedEmployees([]);
        setTimeout(() => navigate("/EmployeeDashboard"), 3000);
      }
    } catch (err) {
      console.error("Error sending notification:", err);
      setSuccess("Notification sent successfully");
      setShowSuccessSnackbar(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Send Notification"
        subtitle="Write a message and select recipients to notify"
        icon={<NotificationsActiveIcon />}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* LEFT - Notification Form */}
        <Paper
          elevation={0}
          sx={{
            flex: 1.2,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 2,
              mb: 2.5,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            Compose Notification
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#667eea", mb: 2 }} />
              <Typography variant="body2" sx={{ color: "#8b8fa3" }}>
                Loading employee data...
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              {/* Message Input */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
                >
                  Message *
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  size="small"
                  minRows={4}
                  placeholder="Write your notification message..."
                  value={message}
                  onChange={handleChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      "& fieldset": { borderColor: "#eef0f6" },
                      "&:hover fieldset": { borderColor: "#8b8fa3" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                        borderWidth: 1.5,
                      },
                    },
                  }}
                />
              </Box>

              {/* Recipients */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
                >
                  Recipients *
                </Typography>
                <Select
                  isMulti
                  value={selectedEmployees}
                  onChange={handleEmployeeSelect}
                  options={employeeOptions}
                  placeholder="Select employees..."
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  menuPlacement="bottom"
                  maxMenuHeight={240}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      border: state.isFocused
                        ? "1.5px solid #667eea"
                        : "1px solid #eef0f6",
                      boxShadow: "none",
                      minHeight: "40px",
                      borderRadius: 8,
                      padding: "2px 4px",
                      fontSize: "0.875rem",
                      backgroundColor: "#fff",
                      "&:hover": { borderColor: "#8b8fa3" },
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: "#8b8fa3",
                      padding: "6px",
                    }),
                    indicatorSeparator: () => ({ display: "none" }),
                    menu: (base) => ({
                      ...base,
                      border: "1px solid #eef0f6",
                      borderRadius: 8,
                      marginTop: 4,
                      zIndex: 1301,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: 240,
                      overflowY: "auto",
                      paddingTop: 4,
                      paddingBottom: 4,
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "#EBF0FF"
                        : isFocused
                        ? "#f5f7fb"
                        : "transparent",
                      color: "#1a1a2e",
                      padding: "9px 12px",
                      minHeight: 40,
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: "0.875rem",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#EBF0FF",
                      borderRadius: 4,
                      padding: "1px 4px",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#667eea",
                      fontSize: "0.8rem",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#667eea",
                      "&:hover": {
                        backgroundColor: "#667eea",
                        color: "#fff",
                      },
                    }),
                  }}
                />
              </Box>

              {/* Selected Employees List */}
              {selectedEmployees?.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #eef0f6",
                    borderRadius: "8px",
                    bgcolor: "#f5f7fb",
                    maxHeight: 150,
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#5a5f7a",
                      mb: 0.5,
                    }}
                  >
                    Selected Recipients ({selectedEmployees.length})
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { fontSize: "0.825rem", color: "#5a5f7a", mb: 0.25 } }}
                  >
                    {selectedEmployees.map((employee) => (
                      <li key={employee.value}>{employee.label}</li>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Alerts */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.875rem" }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ borderRadius: "8px", fontSize: "0.875rem" }}>
                  {success}
                </Alert>
              )}

              {/* Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  pt: 1,
                  borderTop: "1px solid #eef0f6",
                  mt: 0.5,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/EmployeeDashboard")}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderColor: "#CBD5E1",
                    color: "#5a5f7a",
                    px: 3,
                    "&:hover": {
                      borderColor: "#8b8fa3",
                      bgcolor: "#f5f7fb",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SendIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    px: 3,
                    boxShadow: "none",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                      boxShadow: "none",
                    },
                    "&:disabled": {
                      bgcolor: "#8b8fa3",
                      color: "#fff",
                    },
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send Notification"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* RIGHT - Quick Tips */}
        <Paper
          elevation={0}
          sx={{
            flex: 0.8,
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
            display: { xs: "none", md: "block" },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 2,
              mb: 2,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            Quick Tips
          </Typography>

          {[
            {
              title: "Target Your Audience",
              desc: "Select specific employees or use 'Select All' for company-wide announcements.",
            },
            {
              title: "Be Clear & Concise",
              desc: "Keep your notification message brief and actionable for best engagement.",
            },
            {
              title: "Track Delivery",
              desc: "All sent notifications are logged and can be tracked from the dashboard.",
            },
          ].map((item, idx) => (
            <Box
              key={idx}
              sx={{
                py: 1.5,
                borderBottom:
                  idx < 2 ? "1px solid #f0f2f8" : "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#1a1a2e",
                  mb: 0.25,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8rem", color: "#8b8fa3", lineHeight: 1.5 }}
              >
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        message="Notification sent successfully!"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </PageContainer>
  );
};

export default NotificationForm;
